import {
  AddonCategory,
  AddonOption,
  Aggregate,
  PowerSupplyOptions,
  GraphCurve,
} from 'Types/Aggregates';
import { TFunction } from 'react-i18next';
import moment from 'moment';
import { getFilterValue } from 'utils';
import { Project, Result } from '../Types/Project';
import { ReheaterValues, hasActive } from '../Types/Aggregates';

import axios, { throwAxiosError } from './request';
import { groupAddons } from './addons';

const endpoint = '/results/';

interface PaginatedResults {
  count: number;
  next: number;
  previous: number;
  results: Result[];
}

interface ResultsQueryParams {
  project_id?: string | number;
  type?: 'user_project' | 'quote_request';
}

export const getResults = async (
  params?: ResultsQueryParams
): Promise<PaginatedResults | null> => {
  try {
    const res = await axios.get(endpoint, { params });
    return res.data as PaginatedResults;
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const createResult = async (p: Result): Promise<Result | null> => {
  try {
    const { data } = await axios.post(endpoint, p);
    if (data) {
      return data as Result;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const updateResult = async (p: Result): Promise<Result | null> => {
  try {
    const { data } = await axios.patch(`${endpoint}${p.id}/`, p);
    if (data) {
      return data as Result;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const getResult = async (
  id: string | number
): Promise<Result | null> => {
  try {
    const { data } = await axios.get(`${endpoint}${id}/`);
    if (data) {
      return data as Result;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const deleteResult = async (id: string | number): Promise<null> => {
  try {
    await axios.delete(`${endpoint}${id}/`);
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const withdrawResult = async (
  id: string | number
): Promise<Result | null> => {
  try {
    const { data } = await axios.patch(`${endpoint}${id}/`, {
      status: 'withdrawn',
    });
    if (data) {
      return data as Result;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const markResultQuoted = async (
  id: string | number
): Promise<Result | null> => {
  try {
    const { data } = await axios.patch(`${endpoint}${id}/`, {
      status: 'quoted',
    });
    if (data) {
      return data as Result;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

export const markResultDeclined = async (
  id: string | number
): Promise<Result | null> => {
  try {
    const { data } = await axios.patch(`${endpoint}${id}/`, {
      status: 'declined',
    });
    if (data) {
      return data as Result;
    }
  } catch (error) {
    throwAxiosError(error);
  }
  return null;
};

const getMotorValue = (
  valves: ReheaterValues[],
  inputs: any
): ReheaterValues | null => {
  let val = valves.find((x) => x.key === inputs.kvs_size);
  if (!val) {
    val = valves.find((x) => x.standard);
  }
  if (val) {
    return val; // `Kvs=${val.Kvs.toFixed(1)}, N=${(val.N * 100).toFixed(1)}`;
  }
  return null; // '-';
};

export const parseAudioCalculations = (
  aggregate: Aggregate,
  t: TFunction<'translation', undefined>,
  heatingMode: boolean = true,
  cleanFilters: boolean = false
): Array<number[]> => {
  const rowFreqs = [
    63,
    125,
    250,
    500,
    1000,
    2000,
    4000,
    t('resultsPage.audioCalculations.total'),
  ];
  const rows: Array<any> = [];
  const season = heatingMode
    ? aggregate?.system_data?.heating
    : aggregate?.system_data?.cooling;

  let octaveBands = season?.octave_bands || {};
  if (cleanFilters) {
    octaveBands = season?.octave_bands_clean;
  }

  for (let i = 0; i < rowFreqs.length; i += 1) {
    rows[i] = [
      rowFreqs[i],
      octaveBands.surface[i].toFixed(1),
      octaveBands.inlet[i].toFixed(1),
      octaveBands.discharge[i].toFixed(1),
      octaveBands.ambient[i].toFixed(1),
      octaveBands.exhaust[i].toFixed(1),
    ];
  }
  return rows;
};

const parseCurve = (
  id: string,
  yCoords: Array<number | null>,
  xCoords: Array<number | null>,
  color: string = 'black',
  yFactor: number = 1,
  xFactor: number = 3600
): GraphCurve => {
  const values = {
    id,
    color,
    data:
      yCoords?.map((val, idx) => ({
        x: (xCoords[idx] || 0) * xFactor,
        y: val ? val * yFactor : val,
      })) || [],
  };
  values.data = values.data.filter((val) => val.x !== null && val.y !== null);
  return values;
};

export const parseCapacityData = (aggregate: Aggregate): Array<GraphCurve> => {
  const airCapacity = aggregate?.system_data?.heating?.air_capacity;
  if (!airCapacity?.rating) {
    return [];
  }
  const blackCircle = airCapacity.rating.find((r) => r.dirt_level === 0);
  const redCircle = airCapacity.rating.find((r) => r.dirt_level === 0.5);
  const redCross = airCapacity.rating.find((r) => r.dirt_level === 1);
  const factor = 3600;

  const arr: GraphCurve[] = [
    {
      id: 'result',
      data: [
        { x: (redCross?.qv || 0) * factor, y: redCross?.dp || 0 },
        { x: (redCircle?.qv || 0) * factor, y: redCircle?.dp || 0 },
        { x: (blackCircle?.qv || 0) * factor, y: blackCircle?.dp || 0 },
      ],
    },
    parseCurve('2500 J/㎥', airCapacity.sfp2500, airCapacity.qv),
    parseCurve('2100 J/㎥', airCapacity.sfp2100, airCapacity.qv),
    parseCurve('1800 J/㎥', airCapacity.sfp1800, airCapacity.qv),
    parseCurve('1500 J/㎥', airCapacity.sfp1500, airCapacity.qv),
    parseCurve('MaxPa', airCapacity.dp_max, airCapacity.qv),
    {
      id: 'qv_2_8_ms',
      data: [
        {
          x: (airCapacity.qv_2_8_ms || 0) * factor,
          y: 0,
        },
        {
          x: (airCapacity.qv_2_8_ms || 0) * factor,
          y: airCapacity.qv_fixed_max_dp || 0,
        },
      ],
      text: '*v. 2,8 m/s',
    },
    {
      id: 'qv_3_2_ms',
      data: [
        {
          x: (airCapacity.qv_3_2_ms || 0) * factor,
          y: 0,
        },
        {
          x: (airCapacity.qv_3_2_ms || 0) * factor,
          y: airCapacity.qv_fixed_max_dp || 0,
        },
      ],
      text: '*v. 3,2 m/s',
    },
  ];

  if (airCapacity.showRatingSummer) {
    const sBlackCircle = airCapacity.rating_summer.find(
      (r) => r.dirt_level === 0
    );
    const sBlueCircle = airCapacity.rating_summer.find(
      (r) => r.dirt_level === 0.5
    );
    const sBlueCross = airCapacity.rating_summer.find(
      (r) => r.dirt_level === 1
    );

    arr.push({
      id: 'result_summer',
      data: [
        { x: (sBlueCross?.qv || 0) * factor, y: sBlueCross?.dp || 0 },
        { x: (sBlueCircle?.qv || 0) * factor, y: sBlueCircle?.dp || 0 },
        { x: (sBlackCircle?.qv || 0) * factor, y: sBlackCircle?.dp || 0 },
      ],
    });
  }

  // filter out all Pa values > 800
  for (let i = 0; i < arr.length; i += 1) {
    arr[i].data = arr[i].data.filter((data) => {
      if (data?.y && data.y > 800) {
        if (arr[i].id === 'qv_3_2_ms' || arr[i].id === 'qv_2_8_ms') {
          // eslint-disable-next-line no-param-reassign
          data.y = 800;
        } else {
          return false;
        }
      }
      return true;
    });
  }
  return arr;
};

export const parseHeatReclaimCurve = (
  aggregate: Aggregate
): GraphCurve[] | null => {
  const heatReclaim = aggregate.system_data?.heating?.heat_reclaim_curve;
  const showHeatReclaim = aggregate.system_data?.heating?.show_heat_reclaim;
  if (!heatReclaim || !showHeatReclaim || heatReclaim?.qv_def < 0.01) {
    return null;
  }
  const xFactor = 3600;
  const yFactor = 100;

  return [
    parseCurve(
      'temperature',
      heatReclaim.eff,
      heatReclaim.qv,
      'green',
      yFactor,
      xFactor
    ),
    {
      id: 'result',
      data: [
        {
          x: (heatReclaim?.qv_def || 0) * xFactor,
          y: (heatReclaim?.eff_def || 0) * yFactor,
        },
      ],
    },
  ];
};

export interface InputRow {
  key: string;
  title: string;
  values: string[];
}

export interface ResultSet {
  [key: string]: any;
}

export const parseAdditionalData = (
  t: TFunction<'translation', undefined>,
  aggregate: Aggregate,
  inputs: any
): ResultSet | null => {
  const result: ResultSet = {
    reheater_type: inputs?.['reheater_config.reheater_type'],
    power_supply: inputs?.power_supply,
  };

  // Translate power supply enum:
  switch (result.power_supply) {
    case 'TWOTHIRTY':
      result.power_supply = '230 V';
      break;
    case 'FOURHUNDRED':
      result.power_supply = '400 V';
      break;
    default:
      break;
  }

  const valve = getMotorValue(
    aggregate?.system_data.heating.reheater.valves,
    inputs
  );

  const elReheater = aggregate?.electric_reheaters?.find(
    (o) => o.id === inputs?.['reheater_config.electric_reheater_id']
  );

  switch (result.reheater_type) {
    case 'WATER':
      // Translate enums:
      result.reheater_type = t('productPage.winterTemp.reheatWater');
      switch (inputs?.['reheater_config.coil_media']) {
        case 'ETYLENEGLYCOL':
          result.reheater_media = t(
            'productPage.winterTemp.reheatEtyleneGlycol'
          );

          result.reheater_concentration = `${(
            inputs?.['reheater_config.coil_concentration'] || 0
          ).toFixed(1)} %`;
          break;
        case 'PROPYLENEGLYCOL':
          result.reheater_media = t(
            'productPage.winterTemp.reheatPropyleneGlycol'
          );
          result.reheater_concentration = `${(
            inputs?.['reheater_config.coil_concentration'] || 0
          ).toFixed(1)} %`;
          break;
        default:
          result.reheater_media = t('productPage.winterTemp.water');
          break;
      }

      result.reheater_dp_coil = `${(
        aggregate?.system_data?.heating?.reheater?.dp_coil || 0
      ).toFixed(0)} Pa`;

      result.reheater_T_supply = `${(
        inputs?.['reheater_config.coil_supply'] || 0
      ).toFixed(1)} °C`;

      result.reheater_T_return = `${(
        inputs?.['reheater_config.coil_return'] || 0
      ).toFixed(1)} °C`;

      result.reheater_Q = `${(
        (aggregate?.system_data?.heating?.reheater?.Q || 0) / 1000
      ).toFixed(2)} kW`;

      if (valve) {
        result.reheater_kvs_size = `Kvs=${valve.Kvs.toFixed(1)}, N=${(
          valve.N * 100
        ).toFixed(1)}`;
        result.reheater_dp_valve = `${(valve.dp_tot - valve.dp_w).toFixed(
          0
        )} Pa`;
      }

      result.reheater_T_before = `${(
        (aggregate?.system_data?.heating?.supply?.T_out || 0) - 273.15
      ).toFixed(1)} °C`;

      result.reheater_T_after = `${(
        (aggregate?.system_data?.heating?.reheater?.T_out || 0) - 273.15
      ).toFixed(1)} °C`;
      break;
    case 'ELECTRIC':
      // Translate enums:
      result.reheater_type = t('productPage.winterTemp.reheatElectric');

      result.reheater_Q = `${(
        (aggregate?.system_data?.heating?.reheater?.Q || 0) / 1000
      ).toFixed(2)} kW`;

      result.reheater_electric_name = `${(
        (elReheater?.capacity || 0) / 1000
      ).toFixed(2)} kW (${
        PowerSupplyOptions.find(
          (x) => x.key === (elReheater?.power_supply || 'BOTH')
        )?.text || `400V ${t('common.and')} 230V`
      })`;

      result.reheater_T_before = `${(
        (aggregate?.system_data?.heating?.supply?.T_out || 0) - 273.15
      ).toFixed(1)} °C`;

      result.reheater_T_after = `${(
        (aggregate?.system_data?.heating?.reheater?.T_out || 0) - 273.15
      ).toFixed(1)} °C`;
      break;
    default:
      // Translate enums:
      result.reheater_type = t('common.none');
      break;
  }

  return result;
};

export const formatAggregateForResults = (
  aggregate: Aggregate,
  t: TFunction<'translation', undefined>,
  inputs: any,
  project?: Project,
  calulationName?: string,
  availableAddons?: AddonOption[],
  alternativeToName?: string | null
) => {
  const audioCalculations = parseAudioCalculations(aggregate, t, true, false);
  const audioCalculationsSummer = parseAudioCalculations(
    aggregate,
    t,
    false,
    false
  );

  const audioCalculationsClean = parseAudioCalculations(
    aggregate,
    t,
    true,
    true
  );
  const audioCalculationsCleanSummer = parseAudioCalculations(
    aggregate,
    t,
    false,
    true
  );
  const stubPlacementSet =
    inputs?.['stub_placement.version'] === 'left'
      ? aggregate?.stub_placements_left
      : aggregate?.stub_placements_right;
  const stubPlacement = stubPlacementSet?.stub_placements?.find(
    (sp) => sp.id === inputs['stub_placement.placement_id']
  );

  // Resolve addon IDs to full addon objects from availableAddons
  // inputs.addons may be full objects (legacy) or just IDs (new format)
  let resolvedAddons = inputs?.addons;
  // Resolve and validate addons against availableAddons
  // This ensures only addons valid for the current aggregate are included
  if (resolvedAddons?.length && availableAddons?.length) {
    const availableIds = new Set(availableAddons.map((a) => a.id));
    const firstAddon = resolvedAddons[0];

    if (
      typeof firstAddon === 'number' ||
      (typeof firstAddon === 'string' && !firstAddon.includes('{'))
    ) {
      // Addons are IDs - resolve to full objects
      resolvedAddons = resolvedAddons
        .map((id: number | string) =>
          availableAddons.find((a) => a.id === Number(id))
        )
        .filter(Boolean);
    } else {
      // Addons are full objects - filter to only those valid for this aggregate
      resolvedAddons = resolvedAddons.filter(
        (addon: AddonOption) => addon?.id && availableIds.has(addon.id)
      );
    }
  } else if (resolvedAddons?.length && !availableAddons?.length) {
    // No availableAddons to validate against - can't show any addons
    resolvedAddons = [];
  }

  const automatics: AddonCategory[] = groupAddons(resolvedAddons, true);

  const additionalData = parseAdditionalData(t, aggregate, inputs);

  const calculations = {
    title: t(`resultsPage.calculationsPage.header`),
    electricity: {
      title: t(`resultsPage.calculationsPage.powerUseHeatGainEnviromentSound`),
      values: [
        {
          name: t(`resultsPage.calculationsPage.specificPowerUse`),
          value: `${aggregate?.system_data.heating?.SFP.toFixed(0) || 0} J/m3`,
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN1353`
          ),
        },
        {
          name: t(`resultsPage.calculationsPage.dryHeatGain`),
          value: aggregate?.system_data.heating?.show_heat_reclaim
            ? `${(
                (aggregate?.system_data.heating?.heat_reclaim || 0) * 100
              ).toFixed(1)} %`
            : '',
          description: t(`resultsPage.calculationsPage.testedAccordingToEN308`),
        },
        {
          name: t(`resultsPage.calculationsPage.enviromentSound`),
          value: `${aggregate?.system_data.heating?.Lp.toFixed(0)} dB(A)`,
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN9614_2`
          ),
        },
      ],
    },
    heating: {
      title: t(`resultsPage.calculationsPage.heatOutput`),
      values: [
        hasActive(aggregate) && {
          name: t(`productPage.winterTemp.heatEffectTotal`),
          value: `${((aggregate?.system_data.heating?.Qc || 0) / 1000).toFixed(
            2
          )} kW`,
          description: hasActive(aggregate)
            ? t(`resultsPage.calculationsPage.testedAccordingToEN14511`)
            : t(`resultsPage.calculationsPage.testedAccordingToEN308`),
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.heatCOP`),
          value: (aggregate?.system_data.heating?.COP || 0).toFixed(2),
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN14511`
          ),
        },
        {
          name: t(
            `resultsPage.calculationsPage.heatingEffectRotatingHeatExchanger`
          ),
          value: `${(
            (aggregate?.system_data.heating?.Qc_passive || 0) / 1000
          ).toFixed(2)} kW`,
          description: hasActive(aggregate)
            ? t(`resultsPage.calculationsPage.testedAccordingToEN14511`)
            : t(`resultsPage.calculationsPage.testedAccordingToEN308`),
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.heatingEffectHeatPump`),
          value: `${(
            ((aggregate?.system_data.heating?.Qc || 0) -
              (aggregate?.system_data.heating?.Qc_passive || 0)) /
            1000
          ).toFixed(2)} kW`,
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN14511`
          ),
        },
        {
          name: t(`resultsPage.calculationsPage.totalRotorPower`),
          value: `${(aggregate?.system_data.heating?.W_rest || 0).toFixed(
            0
          )} W`,
          description: hasActive(aggregate)
            ? t(`resultsPage.calculationsPage.testedAccordingToEN14511`)
            : t(`resultsPage.calculationsPage.testedAccordingToEN308`),
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.compressorPowerConsumption`),
          value: `${((aggregate?.system_data.heating?.W || 0) / 1000).toFixed(
            2
          )} kW`,
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN14511`
          ),
        },
        hasActive(aggregate) && {
          name: t(
            `resultsPage.calculationsPage.calculatedAirSupplyTemperaturWithHeatPump`
          ),
          value: `${(
            (aggregate.system_data.heating?.supply.T_out || 273.15) - 273.15
          ).toFixed(1)} °C
          ${(
            (aggregate?.system_data.heating?.supply.RH_out || 0) * 100
          ).toFixed(1)}% RH`,
          description: '',
        },
        {
          name: t(
            `resultsPage.calculationsPage.calculatedHeatEffectWaterReheatingSurface`
          ),
          value: `${(
            (aggregate?.system_data.heating?.reheater.Q || 0) / 1000
          ).toFixed(2)} kW`,
          description: '',
        },
        {
          name: t(
            `resultsPage.calculationsPage.calculatedirSupplyTemperaturWithReheatingSurface`
          ),
          value: `${(
            (aggregate.system_data.heating?.reheater.T_out || 273.15) - 273.15
          ).toFixed(1)} °C
          ${(
            (aggregate?.system_data.heating?.reheater.RH_out || 0) * 100
          ).toFixed(1)}% RH`,
          description: '',
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.calculatedEvapTemperature`),
          value: `${(
            (aggregate.system_data.heating?.Te || 273.15) - 273.15
          ).toFixed(1)} °C`,
          description: '',
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.calculatedCondTemperature`),
          value: `${(
            (aggregate.system_data.heating?.Tc || 273.15) - 273.15
          ).toFixed(1)} °C`,
          description: '',
        },
      ],
    },
    cooling: {
      title: t(`resultsPage.calculationsPage.coolingCapacity`),
      values: [
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.totalCoolingPower`),
          value: `${((aggregate?.system_data.cooling?.Qe || 0) / 1000).toFixed(
            2
          )} kW`,
          description: hasActive(aggregate)
            ? t(`resultsPage.calculationsPage.testedAccordingToEN14511`)
            : t(`resultsPage.calculationsPage.testedAccordingToEN308`),
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.totalCoolingCOP`),
          value: aggregate?.system_data.cooling?.EER.toFixed(2),
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN14511`
          ),
        },
        {
          name: t(`resultsPage.calculationsPage.coolingEffectRotaryExchanger`),
          value: `${(
            (aggregate?.system_data.cooling?.Qe_passive || 0) / 1000
          ).toFixed(2)} kW`,
          description: hasActive(aggregate)
            ? t(`resultsPage.calculationsPage.testedAccordingToEN14511`)
            : t(`resultsPage.calculationsPage.testedAccordingToEN308`),
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.coolingEffectHeatPump`),
          value: `${(
            ((aggregate?.system_data.cooling?.Qe || 0) -
              (aggregate?.system_data.cooling?.Qe_passive || 0)) /
            1000
          ).toFixed(2)} kW`,
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN14511`
          ),
        },
        {
          name: t(`resultsPage.calculationsPage.totalRotorPower`),
          value: `${(aggregate?.system_data.cooling?.W_rest || 0).toFixed(
            0
          )} W`,
          description: hasActive(aggregate)
            ? t(`resultsPage.calculationsPage.testedAccordingToEN14511`)
            : t(`resultsPage.calculationsPage.testedAccordingToEN308`),
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.compressorPowerConsumption`),
          value: `${((aggregate?.system_data.cooling?.W || 0) / 1000).toFixed(
            2
          )} kW`,
          description: t(
            `resultsPage.calculationsPage.testedAccordingToEN14511`
          ),
        },
        {
          name: t(
            `resultsPage.calculationsPage.calculatedSupplyAirTemperature`
          ),
          value: `${(
            (aggregate.system_data.cooling?.supply.T_out || 273.15) - 273.15
          ).toFixed(1)} °C
          ${(
            (aggregate?.system_data.cooling?.supply.RH_out || 0) * 100
          ).toFixed(1)}% RH`,
          description: '',
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.calculatedEvapTemperature`),
          value: `${(
            (aggregate.system_data.cooling?.Te || 273.15) - 273.15
          ).toFixed(1)} °C`,
          description: '',
        },
        hasActive(aggregate) && {
          name: t(`resultsPage.calculationsPage.calculatedCondTemperature`),
          value: `${(
            (aggregate.system_data.cooling?.Tc || 273.15) - 273.15
          ).toFixed(1)} °C`,
          description: '',
        },
      ],
    },
    inputs: {
      title: t(`resultsPage.calculationsPage.prerequisitesForTheCalculations`),
      description: t(`resultsPage.calculationsPage.description`),
      values: [
        {
          key: 'airVolumes',
          title: t(`resultsPage.calculationsPage.airVolumes`),
          values: [
            `${t(`resultsPage.calculationsPage.airSupply`)}: ${
              inputs['cooling.ambient.VIn']
            } m3/h ${t('common.at')} ${
              inputs['cooling.ambient.dp_external']
            } ${t('resultsPage.calculationsPage.paExternal')}`,
            `${t(`resultsPage.calculationsPage.exhaustAir`)}: ${
              inputs['cooling.indoor.VIn']
            } m3/h ${t('common.at')} ${
              inputs['cooling.indoor.dp_external']
            } ${t('resultsPage.calculationsPage.paExternal')}`,
          ],
        },
        {
          key: 'filterBox',
          title: t(`resultsPage.calculationsPage.filterBox`),
          values: [
            `${t(`resultsPage.calculationsPage.outdoorAir`)}: ${getFilterValue(
              aggregate,
              inputs,
              'supply_filter'
            )}`,
            `${t(`resultsPage.calculationsPage.finalPressureDropSupply`)}: ${(
              aggregate?.system_data?.cooling?.dp_max_filter_supply || 0
            ).toFixed(0)} Pa`,
            `${t(`resultsPage.calculationsPage.exhaustAir`)}: ${getFilterValue(
              aggregate,
              inputs,
              'discharge_filter'
            )}`,
            ` ${t(`resultsPage.calculationsPage.finalPressureDropExhaust`)}: ${(
              aggregate?.system_data?.cooling?.dp_max_filter_exhaust || 0
            ).toFixed(0)} Pa`,
          ],
        },
        {
          key: 'temperaturePrereq',
          title: t(`resultsPage.calculationsPage.temperaturePrerequisites`),
          values: [
            `${t(`resultsPage.calculationsPage.sommer`)}:`,
            `${t(`resultsPage.calculationsPage.outdoorAir`)}:`,
            `${inputs['cooling.ambient.TIn']} °C ${inputs['cooling.ambient.RHIn']} %RH`,
            `${t(`resultsPage.calculationsPage.exhaustAir`)}:`,
            `${inputs['cooling.indoor.TIn']} °C ${inputs['cooling.indoor.RHIn']} %RH`,
            `${t(`resultsPage.calculationsPage.winter`)}:`,
            `${t(`resultsPage.calculationsPage.outdoorAir`)}:`,
            `${inputs['heating.ambient.TIn']} °C ${inputs['heating.ambient.RHIn']} % RH`,
            `${t(`resultsPage.calculationsPage.exhaustAir`)}:`,
            `${inputs['heating.indoor.TIn']} °C ${inputs['heating.indoor.RHIn']} %RH`,
          ],
        },
      ],
    },
  };

  return {
    date: moment(project?.created_at).format('DD-MM-YYYY'),
    calulationName,
    alternativeToName,
    frontPage: {
      title: `${t('resultsPage.title')} ${aggregate.name}`,
      subTitle: `${t('resultsPage.subTitle')} ${moment(
        project?.created_at
      ).format('DD-MM-YYYY')}`,
      productId: aggregate?.name || '',
      author: t('resultsPage.author'),
      id: aggregate?.name || '',
      image: aggregate?.aggregate_picture || '',
      calculations,
    },
    endPage: {
      address: [
        t('address.name'),
        t('address.lineOne'),
        t('address.lineTwo'),
        t('address.tel'),
        t('address.email'),
        t('address.webAddress'),
      ],
      disclaimer: t('resultsPage.disclaimer'),
    },
    description: {
      title: t('resultsPage.description'),
      productName: aggregate?.name,
      productDescription:
        aggregate?.description /* 
      image: aggregate?.aggregate_picture, */,
    },
    addons: {
      title: t('resultsPage.descriptionAndAddons.aggregateAddonsHeader'),
      description: `${t(
        'resultsPage.descriptionAndAddons.aggregateAddonsDescription'
      )}: ${aggregate.name}`,
      selectedAddons: resolvedAddons?.length ? groupAddons(resolvedAddons) : [],
    },
    calculations,
    audioCalc: {
      title: t(`resultsPage.audioCalculations.headerWinter`),
      subTitle: t(`resultsPage.audioCalculations.audioEffectLevel`),
      airVolumes: [
        `${t('resultsPage.audioCalculations.description.airVolume')}: `,
        `${t('resultsPage.calculationsPage.airSupply')}: ${(
          (aggregate?.system_data?.heating?.octave_bands?.qv_supply || 0) * 3600
        ).toFixed(0)} m3/h ${t(
          'resultsPage.audioCalculations.description.at'
        )} ${aggregate?.system_data.heating?.octave_bands.dp_ext_supply} ${t(
          'resultsPage.calculationsPage.paExternal'
        )}`,
        `${t('resultsPage.calculationsPage.exhaustAir')}: ${(
          (aggregate?.system_data?.heating?.octave_bands?.qv_exhaust || 0) *
          3600
        ).toFixed(0)} m3/h ${t(
          'resultsPage.audioCalculations.description.at'
        )} ${aggregate?.system_data.heating?.octave_bands.dp_ext_exhaust} ${t(
          'resultsPage.calculationsPage.paExternal'
        )}`,
        t(`resultsPage.audioCalculations.description.surface&channelTest`),
      ],
      table: {
        data: audioCalculations.map((arr, idx) => {
          let name;
          if (idx === 0) {
            name = t(`resultsPage.audioCalculations.headers.octaveBandHZ`);
          } else if (idx === 1) {
            name = t(`resultsPage.audioCalculations.headers.surface`);
          } else if (idx === 2) {
            name = t(`resultsPage.audioCalculations.headers.inlet`);
          } else if (idx === 3) {
            name = t(`resultsPage.audioCalculations.headers.discharge`);
          } else if (idx === 4) {
            name = t(`resultsPage.audioCalculations.headers.ambient`);
          } else if (idx === 5) {
            name = t(`resultsPage.audioCalculations.headers.exhaust`);
          }
          return {
            name,
            values: arr,
          };
        }),
        dataDescription: {
          title: t(`resultsPage.audioCalculations.description.dirtyFilters`),
        },
      },
      tableClean: {
        data: audioCalculationsClean.map((arr, idx) => {
          let name;
          if (idx === 0) {
            name = t(`resultsPage.audioCalculations.headers.octaveBandHZ`);
          } else if (idx === 1) {
            name = t(`resultsPage.audioCalculations.headers.surface`);
          } else if (idx === 2) {
            name = t(`resultsPage.audioCalculations.headers.inlet`);
          } else if (idx === 3) {
            name = t(`resultsPage.audioCalculations.headers.discharge`);
          } else if (idx === 4) {
            name = t(`resultsPage.audioCalculations.headers.ambient`);
          } else if (idx === 5) {
            name = t(`resultsPage.audioCalculations.headers.exhaust`);
          }
          return {
            name,
            values: arr,
          };
        }),
        dataDescription: {
          title: t('resultsPage.audioCalculations.description.cleanFilters'),
        },
      },
    },
    audioCalcSummer: {
      title: t(`resultsPage.audioCalculations.headerSummer`),
      subTitle: t(`resultsPage.audioCalculations.audioEffectLevel`),
      airVolumes: [
        `${t('resultsPage.audioCalculations.description.airVolume')}: `,
        `${t('resultsPage.calculationsPage.exhaustAir')}: ${(
          (aggregate?.system_data?.cooling?.octave_bands?.qv_exhaust || 0) *
          3600
        ).toFixed(0)} m3/h ${t(
          'resultsPage.audioCalculations.description.at'
        )} ${aggregate?.system_data.cooling?.octave_bands.dp_ext_exhaust} ${t(
          'resultsPage.calculationsPage.paExternal'
        )}`,
        `${t('resultsPage.calculationsPage.airSupply')}: ${(
          (aggregate?.system_data?.cooling?.octave_bands?.qv_supply || 0) * 3600
        ).toFixed(0)} m3/h ${t(
          'resultsPage.audioCalculations.description.at'
        )} ${aggregate?.system_data.cooling?.octave_bands.dp_ext_supply} ${t(
          'resultsPage.calculationsPage.paExternal'
        )}`,
        t(`resultsPage.audioCalculations.description.surface&channelTest`),
      ],
      table: {
        data: audioCalculationsSummer.map((arr, idx) => {
          let name;
          if (idx === 0) {
            name = t(`resultsPage.audioCalculations.headers.octaveBandHZ`);
          } else if (idx === 1) {
            name = t(`resultsPage.audioCalculations.headers.surface`);
          } else if (idx === 2) {
            name = t(`resultsPage.audioCalculations.headers.inlet`);
          } else if (idx === 3) {
            name = t(`resultsPage.audioCalculations.headers.discharge`);
          } else if (idx === 4) {
            name = t(`resultsPage.audioCalculations.headers.ambient`);
          } else if (idx === 5) {
            name = t(`resultsPage.audioCalculations.headers.exhaust`);
          }
          return {
            name,
            values: arr,
          };
        }),
        dataDescription: {
          title: t(`resultsPage.audioCalculations.description.dirtyFilters`),
        },
      },
      tableClean: {
        data: audioCalculationsCleanSummer.map((arr, idx) => {
          let name;
          if (idx === 0) {
            name = t(`resultsPage.audioCalculations.headers.octaveBandHZ`);
          } else if (idx === 1) {
            name = t(`resultsPage.audioCalculations.headers.surface`);
          } else if (idx === 2) {
            name = t(`resultsPage.audioCalculations.headers.inlet`);
          } else if (idx === 3) {
            name = t(`resultsPage.audioCalculations.headers.discharge`);
          } else if (idx === 4) {
            name = t(`resultsPage.audioCalculations.headers.ambient`);
          } else if (idx === 5) {
            name = t(`resultsPage.audioCalculations.headers.exhaust`);
          }
          return {
            name,
            values: arr,
          };
        }),
        dataDescription: {
          title: t(`resultsPage.audioCalculations.description.cleanFilters`),
        },
      },
    },
    graphs: {
      title: 'Kurver',
      capacity: {
        title: t(`resultsPage.capacityCurve.title`),
        subTitle: aggregate.name,
        data: parseCapacityData(aggregate),
        descriptionList: [
          t(`resultsPage.capacityCurve.descriptionBlack`),
          t(`resultsPage.capacityCurve.descriptionRedCircle`),
          t(`resultsPage.capacityCurve.descriptionRedCross`),
        ],
      },
      heatReclaim: {
        title: t(`resultsPage.heatReclaim.title`),
        data: parseHeatReclaimCurve(aggregate),
        subTitle: aggregate.name,
        descriptionList: [t(`resultsPage.heatReclaim.description`)],
      },
    },
    placement: {
      title: t(`resultsPage.stubPlacementPage.header`),
      imageUrl: stubPlacement?.detailed_drawing,
      descriptionList: [
        inputs?.stub_placement?.version === 'left'
          ? t(`resultsPage.stubPlacementPage.descriptionLeft`)
          : t(`resultsPage.stubPlacementPage.descriptionRight`),
        `${t('productPage.placement.airOut')}: ${t(
          `resultsPage.stubPlacementPage.stubPlacementLabel`
        )}: ${stubPlacement?.fresh_air}`,
        `${t('productPage.placement.airTo')}: ${t(
          `resultsPage.stubPlacementPage.stubPlacementLabel`
        )}: ${stubPlacement?.supply_air}`,
        `${t('productPage.placement.airFrom')}: ${t(
          `resultsPage.stubPlacementPage.stubPlacementLabel`
        )}: ${stubPlacement?.discharge_air}`,
        `${t('productPage.placement.return')}: ${t(
          `resultsPage.stubPlacementPage.stubPlacementLabel`
        )}: ${stubPlacement?.extract_air}`,
      ],
    },
    additional: additionalData,
    automatics: {
      title: 'Automatik',
      sections: [],
      addonCategories: automatics,
    },
  };
};
